import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Trash2, Plus, Calendar, CheckCircle, PlayCircle } from 'lucide-react';
import { API } from '../App';

export default function PresetTasksManagement() {
  const [presets, setPresets] = useState([]);
  const [filteredPresets, setFilteredPresets] = useState([]);
  const [selectedType, setSelectedType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [autoPublishCount, setAutoPublishCount] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    task_type: 'physical',
    points: 1
  });

  useEffect(() => {
    loadPresets();
    loadAutoPublishStatus();
  }, []);

  useEffect(() => {
    if (selectedType === 'all') {
      setFilteredPresets(presets);
    } else {
      setFilteredPresets(presets.filter(p => p.task_type === selectedType));
    }
  }, [selectedType, presets]);

  const loadPresets = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/preset-tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('Presets loaded:', res.data.length);
      setPresets(res.data);
    } catch (error) {
      console.error('載入預設任務錯誤:', error);
      toast.error('載入預設任務失敗: ' + (error.response?.data?.detail || error.message));
    }
  };

  const loadAutoPublishStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/auto-publish-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAutoPublishCount(res.data.auto_publish_count);
    } catch (error) {
      console.error('載入自動發佈狀態失敗:', error);
    }
  };

  const toggleAutoPublish = async (presetId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/preset-tasks/${presetId}/auto-publish`,
        { auto_publish: !currentStatus },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(`自動發佈已${!currentStatus ? '啟用' : '停用'}`);
      loadPresets();
      loadAutoPublishStatus();
    } catch (error) {
      console.error('切換自動發佈失敗:', error);
      toast.error('操作失敗');
    }
  };

  const handlePublishNow = async () => {
    if (!window.confirm('確定要立即發佈今日的自動任務嗎？')) return;

    setPublishing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/auto-publish-daily-tasks`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(`已發佈 ${res.data.published_count} 個任務`);
    } catch (error) {
      console.error('發佈任務失敗:', error);
      toast.error('發佈失敗');
    } finally {
      setPublishing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/preset-tasks`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('預設任務已新增');
      setDialogOpen(false);
      setFormData({ name: '', description: '', task_type: 'physical', points: 1 });
      loadPresets();
    } catch (error) {
      toast.error('新增失敗');
    }
  };

  const handleDelete = async (presetId, isSystem) => {
    if (isSystem) {
      toast.error('系統預設任務無法刪除');
      return;
    }
    
    if (!window.confirm('確定要刪除這個預設任務嗎？')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/preset-tasks/${presetId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('預設任務已刪除');
      loadPresets();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  const taskTypeLabels = {
    physical: '體能訓練',
    stretching: '柔軟度訓練',
    jumping: '跳躍訓練'
  };

  const taskTypeColors = {
    physical: 'from-red-500 to-orange-500',
    stretching: 'from-purple-500 to-pink-500',
    jumping: 'from-blue-500 to-cyan-500'
  };

  return (
    <div className="space-y-6">
      {/* Header with Auto-Publish Status */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">預設任務管理</h2>
          <p className="text-gray-600 text-sm mt-1">管理教練和管理員可使用的預設任務模板</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            onClick={handlePublishNow}
            disabled={publishing || autoPublishCount === 0}
            variant="outline"
            className="border-green-500 text-green-600 hover:bg-green-50"
          >
            <PlayCircle className="mr-2" size={16} />
            {publishing ? '發佈中...' : '立即發佈今日任務'}
          </Button>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600">
                <Plus className="mr-2" size={16} />
                新增預設任務
              </Button>
            </DialogTrigger>
            <DialogContent>
            <DialogHeader>
              <DialogTitle>新增預設任務</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">任務類型</label>
                <Select value={formData.task_type} onValueChange={(value) => setFormData({...formData, task_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="physical">體能訓練</SelectItem>
                    <SelectItem value="stretching">柔軟度訓練</SelectItem>
                    <SelectItem value="jumping">跳躍訓練</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">任務名稱</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="例如：深蹲訓練"
                  required
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">任務描述</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="詳細描述任務內容和要求..."
                  rows={4}
                  required
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">積分</label>
                <Input
                  type="number"
                  value={formData.points}
                  onChange={(e) => setFormData({...formData, points: parseInt(e.target.value)})}
                  min="1"
                  max="10"
                  required
                />
              </div>
              
              <Button type="submit" className="w-full">新增</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={selectedType === 'all' ? 'default' : 'outline'}
            onClick={() => setSelectedType('all')}
            size="sm"
          >
            全部 ({presets.length})
          </Button>
          <Button
            variant={selectedType === 'physical' ? 'default' : 'outline'}
            onClick={() => setSelectedType('physical')}
            size="sm"
          >
            體能訓練 ({presets.filter(p => p.task_type === 'physical').length})
          </Button>
          <Button
            variant={selectedType === 'stretching' ? 'default' : 'outline'}
            onClick={() => setSelectedType('stretching')}
            size="sm"
          >
            柔軟度訓練 ({presets.filter(p => p.task_type === 'stretching').length})
          </Button>
        <Button
          variant={selectedType === 'jumping' ? 'default' : 'outline'}
          onClick={() => setSelectedType('jumping')}
          size="sm"
        >
          跳躍訓練 ({presets.filter(p => p.task_type === 'jumping').length})
        </Button>
        </div>
      </div>

      {/* Auto-Publish Status Banner */}
      {autoPublishCount > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-green-600" size={24} />
            <div className="flex-1">
              <p className="font-medium text-green-800">
                每日自動發佈已啟用
              </p>
              <p className="text-sm text-green-600">
                目前有 {autoPublishCount} 個任務設定為每日自動發佈
              </p>
            </div>
            <Calendar className="text-green-600" size={20} />
          </div>
        </div>
      )}

      {/* Presets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPresets.map((preset) => (
          <div key={preset.preset_id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
            <div className={`bg-gradient-to-r ${taskTypeColors[preset.task_type]} p-4 text-white`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium opacity-90">{taskTypeLabels[preset.task_type]}</div>
                {preset.is_system && (
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full">系統預設</span>
                )}
              </div>
              <h3 className="text-lg font-bold mt-2">{preset.name}</h3>
            </div>
            
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-600 line-clamp-3">{preset.description}</p>
              
              {/* Auto-Publish Toggle */}
              <div className="flex items-center justify-between py-2 border-t">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-500" />
                  <span className="text-sm text-gray-700">每日自動發佈</span>
                </div>
                <Switch
                  checked={preset.auto_publish || false}
                  onCheckedChange={() => toggleAutoPublish(preset.preset_id, preset.auto_publish)}
                />
              </div>
              
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
                    {preset.points} 積分
                  </span>
                  {preset.auto_publish && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                      <CheckCircle size={12} />
                      已啟用
                    </span>
                  )}
                </div>
                
                {!preset.is_system && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(preset.preset_id, preset.is_system)}
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredPresets.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">暫無預設任務</p>
        </div>
      )}
    </div>
  );
}
